import type { ReactNode } from 'react'
import { project } from '../lib/geo'
import { WORLD_HEIGHT, WORLD_PATH, WORLD_WIDTH } from './worldPath'
import styles from './WorldPlot.module.css'

/*
 * The shared base map: graticule, land outline, and the cropped viewport.
 * Everything drawn on top -- resolver dots, connecting lines, a single location
 * marker -- is passed as children in the same projected coordinate space, so
 * WorldPlot and LocationMap share one map rather than maintaining two.
 *
 * Equirectangular gives every latitude equal height, so the full -90..90 range
 * spends a third of the canvas on Antarctica and empty Arctic ocean. Cropping to
 * roughly 84N..56S drops that dead space without touching the projection, which
 * stays the one in lib/geo.ts.
 */
export const TOP = project(84, 0, WORLD_WIDTH, WORLD_HEIGHT).y
export const BOTTOM = project(-56, 0, WORLD_WIDTH, WORLD_HEIGHT).y

/** Graticule at 30 degree intervals, in the projected coordinate space. */
const MERIDIANS = [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150]
    .map((lon) => project(0, lon, WORLD_WIDTH, WORLD_HEIGHT).x)
const PARALLELS = [60, 30, 0, -30]
    .map((lat) => project(lat, 0, WORLD_WIDTH, WORLD_HEIGHT).y)

/** Projects a coordinate into the canvas, or undefined when geoip has none. */
export function place(location: { latitude?: number; longitude?: number } | undefined) {
    if (location?.latitude === undefined || location.longitude === undefined) return undefined
    return project(location.latitude, location.longitude, WORLD_WIDTH, WORLD_HEIGHT)
}

interface WorldCanvasProps {
    label: string
    children: ReactNode
}

export default function WorldCanvas({ label, children }: WorldCanvasProps) {
    return (
        <svg
            className={styles.map}
            viewBox={`0 ${TOP} ${WORLD_WIDTH} ${BOTTOM - TOP}`}
            role="img"
            aria-label={label}
            preserveAspectRatio="xMidYMid meet"
        >
            {/* A graticule under the land, so the map reads as a projection rather
                than a decorative blob. Pure geometry, no data. */}
            <g className={styles.grid} aria-hidden="true">
                {MERIDIANS.map((x) => (
                    <line key={`m${x}`} x1={x} y1={0} x2={x} y2={WORLD_HEIGHT} />
                ))}
                {PARALLELS.map((y) => (
                    <line key={`p${y}`} x1={0} y1={y} x2={WORLD_WIDTH} y2={y} />
                ))}
            </g>

            <path className={styles.land} d={WORLD_PATH} />

            {children}
        </svg>
    )
}
